import { PageWrapper } from '@components'
import {
  LABELS,
  LIFECYCLE_STATUS_LABELS,
  PAYMENT_STATUS_LABELS,
  ROUTE_PARTS,
  TOOLTIPS,
  URL_PATHS,
  WARNINGS,
} from '@constants'
import { useIsMobile } from '@hooks/useIsMobile'
import { useStore } from '@hooks/useStore'
import { Add, Close, Delete, Download, FilterList, Settings } from '@mui/icons-material'
import {
  Badge,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
} from '@mui/material'
import {
  GridRow,
  GridRowParams,
  GridRowSelectionModel,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid'
import { observer } from 'mobx-react-lite'
import { useCallback, useMemo, useState } from 'react'
import { generatePath, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { OvDataGrid } from 'src/components/core/ov-data-grid/ov-data-grid'
import { POLICIES } from 'src/constants/policies.constants'
import { MobileDocumentRow } from 'src/partials/index.partials'

export const InvoiceList = observer(() => {
  const { invoice, policies, ui } = useStore()
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const [selectedYear, setSelectedYear] = useState<number | ''>('')
  const [selectedQuarter, setSelectedQuarter] = useState<number | ''>('')
  const [selectedPaymentStatus, setSelectedPaymentStatus] = useState<string>('')
  const [selectedLifecycleStatus, setSelectedLifecycleStatus] = useState<string>('')
  const [selectedInvoices, setSelectedInvoices] = useState<GridRowSelectionModel>([])
  const [filterModalOpen, setFilterModalOpen] = useState(false)

  const handleRowClick = (params: GridRowParams) => {
    if (!params.id) return
    const path = generatePath(URL_PATHS.INVOICE_DETAIL, { id: params.id })
    return navigate(path)
  }

  const handleNewClick = async () => {
    navigate(URL_PATHS.INVOICE_CREATE)
  }

  const canCreate = useMemo(() => {
    return policies.can(POLICIES.DOCUMENT.CREATE)
  }, [policies.current_policies])

  // Generate year options (current year and 5 years back)
  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear()
    return Array.from({ length: 6 }, (_, i) => currentYear - i)
  }, [])

  // Filter params to pass to data grid
  const filterParams = useMemo(() => {
    const params: Record<string, any> = {}
    if (selectedYear) params.year = selectedYear
    if (selectedQuarter) params.quarter = selectedQuarter
    if (selectedPaymentStatus) params.payment_status = selectedPaymentStatus
    if (selectedLifecycleStatus) params.lifecycle_status = selectedLifecycleStatus
    return params
  }, [selectedYear, selectedQuarter, selectedPaymentStatus, selectedLifecycleStatus])

  // Count all active filters (any filter that has a value set)
  const activeFilterCount = useMemo(() => {
    let count = 0
    // Count year filter if selected
    if (selectedYear !== '') count++
    // Count quarter filter if selected
    if (selectedQuarter !== '') count++
    // Count payment status if selected
    if (selectedPaymentStatus) count++
    // Count lifecycle status if selected
    if (selectedLifecycleStatus) count++
    return count
  }, [selectedYear, selectedQuarter, selectedPaymentStatus, selectedLifecycleStatus])

  const handleClearFilters = () => {
    setSelectedYear('')
    setSelectedQuarter('')
    setSelectedPaymentStatus('')
    setSelectedLifecycleStatus('')
    setFilterModalOpen(false)
  }

  // Check if all selected invoices are finalized
  const canDownloadSelectedInvoices = useMemo(() => {
    if (selectedInvoices.length === 0) return false

    // Check if all selected invoices are finalized
    const allFinalized = selectedInvoices.every(id => {
      const invoiceRow = invoice.data_grid.current_rows.find((row: any) => row.id === id)
      return invoiceRow?.is_finalised === true || invoiceRow?.lifecycle_status === 'finalized'
    })

    return allFinalized
  }, [selectedInvoices, invoice.data_grid.current_rows])

  // Check if all selected invoices are drafts (can be deleted)
  const canDeleteSelectedInvoices = useMemo(() => {
    if (selectedInvoices.length === 0) return false

    // Check if all selected invoices are drafts
    const allDrafts = selectedInvoices.every(id => {
      const invoiceRow = invoice.data_grid.current_rows.find((row: any) => row.id === id)
      return invoiceRow?.is_finalised === false || invoiceRow?.lifecycle_status === 'draft'
    })

    return allDrafts
  }, [selectedInvoices, invoice.data_grid.current_rows])

  const handleBulkDownload = async () => {
    if (selectedInvoices.length === 0) {
      ui.handleToaster(t('errors:select_at_least_one_invoice'), { type: 'warning' })
      return
    }

    try {
      await invoice.downloadBulkZip(selectedInvoices as number[])
      ui.handleToaster(t('success:invoices_downloading'), { type: 'success' })
    } catch (e: any) {
      ui.handleToaster(e.message || t('errors:download_failed'), { type: 'error' })
    }
  }

  const handleBulkDelete = async () => {
    if (selectedInvoices.length === 0) {
      ui.handleToaster(t('errors:select_at_least_one_invoice'), { type: 'warning' })
      return
    }

    try {
      // Delete all selected invoices in bulk (store handles confirmation)
      await invoice.deleteBulk(selectedInvoices as number[])

      // Clear selection and refresh the list
      setSelectedInvoices([])
      await invoice.data_grid.getData(filterParams, isMobile)

      const count = selectedInvoices.length
      ui.handleToaster(
        count === 1 ? t('success:invoices_deleted', { count }) : t('success:invoices_deleted_plural', { count }),
        { type: 'success' }
      )
    } catch (e: any) {
      // User cancelled or error occurred
      if (e?.message) {
        ui.handleToaster(e.message || t('errors:delete_failed'), { type: 'error' })
      }
    }
  }

  const CustomRow = useCallback(
    (props: any) =>
      isMobile ? (
        <MobileDocumentRow
          {...props.row}
          type="invoice"
          onRowClick={handleRowClick}
        />
      ) : (
        <GridRow {...props} />
      ),
    [isMobile]
  )

  const CustomToolbar = useCallback(() => {
    return (
      <GridToolbarContainer
        sx={({ palette }) => ({
          p: 2,
          borderBottom: '2px solid',
          borderBottomColor: palette.divider,
          gap: 2,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
        })}>
        <GridToolbarQuickFilter
          variant="outlined"
          size="small"
          placeholder={LABELS.SEARCH}
          sx={{
            minWidth: isMobile ? '100%' : 200,
          }}
        />
        {!isMobile && <GridToolbarDensitySelector />}
        <Box sx={{ flexGrow: 1 }} />
        {isMobile && activeFilterCount > 0 && (
          <Tooltip title={TOOLTIPS.CLEAR_ALL_FILTERS}>
            <Button
              size="small"
              variant="text"
              onClick={handleClearFilters}
              sx={{ mr: 1 }}>
              Filters wissen ({activeFilterCount})
            </Button>
          </Tooltip>
        )}
        {isMobile && (
          <Tooltip title={TOOLTIPS.OPEN_FILTERS}>
            <Button
              size="small"
              variant="text"
              onClick={() => setFilterModalOpen(true)}
              endIcon={
                <Badge
                  badgeContent={activeFilterCount}
                  color="error"
                  invisible={activeFilterCount === 0}>
                  <FilterList />
                </Badge>
              }>
              Filters
            </Button>
          </Tooltip>
        )}
        {!isMobile && (
          <>
            <FormControl
              size="small"
              sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={selectedLifecycleStatus}
                label="Status"
                onChange={e => setSelectedLifecycleStatus(e.target.value)}>
                <MenuItem value="">
                  <em>Alle statussen</em>
                </MenuItem>
                {Object.entries(LIFECYCLE_STATUS_LABELS).map(([value, label]) => (
                  <MenuItem
                    key={value}
                    value={value}>
                    {String(label)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              size="small"
              sx={{ minWidth: 160 }}>
              <InputLabel>{LABELS.INVOICE_STATUS}</InputLabel>
              <Select
                value={selectedPaymentStatus}
                label={LABELS.INVOICE_STATUS}
                onChange={e => setSelectedPaymentStatus(e.target.value)}>
                <MenuItem value="">
                  <em>Alle statussen</em>
                </MenuItem>
                {Object.entries(PAYMENT_STATUS_LABELS).map(([value, label]) => (
                  <MenuItem
                    key={value}
                    value={value}>
                    {String(label)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              size="small"
              sx={{ minWidth: 120 }}>
              <InputLabel>Jaar</InputLabel>
              <Select
                value={selectedYear}
                label="Jaar"
                onChange={e => setSelectedYear(e.target.value as number)}>
                <MenuItem value="">
                  <em>Alle jaren</em>
                </MenuItem>
                {yearOptions.map(year => (
                  <MenuItem
                    key={year}
                    value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              size="small"
              sx={{ minWidth: 120 }}>
              <InputLabel>Kwartaal</InputLabel>
              <Select
                value={selectedQuarter}
                label="Kwartaal"
                onChange={e => setSelectedQuarter(e.target.value as number)}>
                <MenuItem value="">
                  <em>Alle kwartalen</em>
                </MenuItem>
                <MenuItem value={1}>Q1 (Jan-Mrt)</MenuItem>
                <MenuItem value={2}>Q2 (Apr-Jun)</MenuItem>
                <MenuItem value={3}>Q3 (Jul-Sep)</MenuItem>
                <MenuItem value={4}>Q4 (Okt-Dec)</MenuItem>
              </Select>
            </FormControl>
          </>
        )}
        {canDownloadSelectedInvoices && (
          <Tooltip title={TOOLTIPS.BULK_DOWNLOAD_INVOICES}>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              endIcon={<Download />}
              onClick={handleBulkDownload}>
              Download {selectedInvoices.length} facturen
            </Button>
          </Tooltip>
        )}
        {canDeleteSelectedInvoices && (
          <Tooltip title={TOOLTIPS.BULK_DELETE_DRAFT_INVOICES}>
            <Button
              variant="outlined"
              color="error"
              size="small"
              endIcon={<Delete />}
              onClick={handleBulkDelete}>
              Verwijder {selectedInvoices.length} conceptfactu{selectedInvoices.length === 1 ? 'ur' : 'ren'}
            </Button>
          </Tooltip>
        )}
      </GridToolbarContainer>
    )
  }, [
    selectedYear,
    selectedQuarter,
    selectedPaymentStatus,
    selectedLifecycleStatus,
    yearOptions,
    selectedInvoices,
    isMobile,
    handleBulkDownload,
    handleBulkDelete,
    canDownloadSelectedInvoices,
    canDeleteSelectedInvoices,
    activeFilterCount,
  ])

  return (
    <>
      <PageWrapper
        allowed={policies.can(POLICIES.DOCUMENT.READ)}
        allowedMessage={WARNINGS.NOT_ALLOWED.NO_COMPANY}
        pageTitle={LABELS.INVOICES}
        actions={
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              width: isMobile ? '100%' : 'auto',
              justifyContent: isMobile ? 'space-between' : 'flex-end',
            }}>
            <Tooltip title={TOOLTIPS.OPEN_INVOICE_SETTINGS}>
              <Box sx={{ flex: isMobile ? 1 : 0 }}>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={!canCreate}
                  onClick={() =>
                    navigate(`/${URL_PATHS.INVOICE_SETTINGS}#${ROUTE_PARTS.INVOICE_LIST}`, {
                      state: { from: 'invoices' },
                    })
                  }
                  endIcon={<Settings />}
                  sx={{ width: isMobile ? '100%' : 'auto' }}>
                  {LABELS.SETTINGS}
                </Button>
              </Box>
            </Tooltip>
            <Tooltip title={TOOLTIPS.CREATE_NEW_INVOICE}>
              <Box sx={{ flex: isMobile ? 1 : 0 }}>
                <Button
                  size="small"
                  variant="contained"
                  disabled={!canCreate}
                  endIcon={<Add />}
                  onClick={handleNewClick}
                  sx={{ width: isMobile ? '100%' : 'auto' }}>
                  Nieuw
                </Button>
              </Box>
            </Tooltip>
          </Box>
        }>
        <OvDataGrid
          slots={{
            row: CustomRow,
            toolbar: CustomToolbar,
          }}
          withToolBar={true}
          onRowClick={handleRowClick}
          store={invoice.data_grid}
          responsiveColumns
          params={filterParams}
          checkboxSelection={!isMobile}
          onRowSelectionModelChange={selection => setSelectedInvoices(selection)}
          rowSelectionModel={selectedInvoices}
          initialState={{
            sorting: {
              sortModel: [{ field: 'issued_at', sort: 'desc' }],
            },
          }}
        />
      </PageWrapper>

      {/* Mobile Filter Modal */}
      <Dialog
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        fullWidth
        maxWidth="sm">
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
          Filters
          <IconButton
            onClick={() => setFilterModalOpen(false)}
            size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}>
            <FormControl
              fullWidth
              size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={selectedLifecycleStatus}
                label="Status"
                onChange={e => setSelectedLifecycleStatus(e.target.value)}>
                <MenuItem value="">
                  <em>Alle statussen</em>
                </MenuItem>
                {Object.entries(LIFECYCLE_STATUS_LABELS).map(([value, label]) => (
                  <MenuItem
                    key={value}
                    value={value}>
                    {String(label)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              fullWidth
              size="small">
              <InputLabel>{LABELS.INVOICE_STATUS}</InputLabel>
              <Select
                value={selectedPaymentStatus}
                label={LABELS.INVOICE_STATUS}
                onChange={e => setSelectedPaymentStatus(e.target.value)}>
                <MenuItem value="">
                  <em>Alle statussen</em>
                </MenuItem>
                {Object.entries(PAYMENT_STATUS_LABELS).map(([value, label]) => (
                  <MenuItem
                    key={value}
                    value={value}>
                    {String(label)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              fullWidth
              size="small">
              <InputLabel>Jaar</InputLabel>
              <Select
                value={selectedYear}
                label="Jaar"
                onChange={e => setSelectedYear(e.target.value as number)}>
                <MenuItem value="">
                  <em>Alle jaren</em>
                </MenuItem>
                {yearOptions.map(year => (
                  <MenuItem
                    key={year}
                    value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              fullWidth
              size="small">
              <InputLabel>Kwartaal</InputLabel>
              <Select
                value={selectedQuarter}
                label="Kwartaal"
                onChange={e => setSelectedQuarter(e.target.value as number)}>
                <MenuItem value="">
                  <em>Alle kwartalen</em>
                </MenuItem>
                <MenuItem value={1}>Q1 (Jan-Mrt)</MenuItem>
                <MenuItem value={2}>Q2 (Apr-Jun)</MenuItem>
                <MenuItem value={3}>Q3 (Jul-Sep)</MenuItem>
                <MenuItem value={4}>Q4 (Okt-Dec)</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Tooltip title={TOOLTIPS.CLEAR_ALL_FILTERS}>
            <Button
              onClick={handleClearFilters}
              variant="outlined"
              fullWidth>
              Alle filters wissen
            </Button>
          </Tooltip>
          <Tooltip title={TOOLTIPS.APPLY_FILTERS}>
            <Button
              onClick={() => setFilterModalOpen(false)}
              variant="contained"
              fullWidth>
              Toepassen
            </Button>
          </Tooltip>
        </DialogActions>
      </Dialog>
    </>
  )
})
